import { useMemo } from "react";
import Calendar from "react-calendar";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import "./Calendar.css";

const getItemKey = (item) =>
    typeof item === "string" ? item : item && item.key;

export const getCalendarEventColor = (color) => {
    if (!color) {
        return "rgb(0, 149, 61)";
    }

    return color.startsWith("#") || color.startsWith("rgb")
        ? color
        : `#${color}`;
};

/**
 * The app's one calendar. `selectedDate` is a single Date by default; pass
 * `selectRange` and it becomes a `[start, end]` pair instead, with
 * `onDateChange` firing once both ends are picked.
 */
export default function NodiantCalendar({
    events = [],
    highlightedEventKeys = [],
    minDate,
    maxDate,
    onDateChange,
    selectedDate,
    selectRange = false,
    spacious = false,
}) {
    const highlightedKeys = useMemo(
        () =>
            new Set(
                Array.from(highlightedEventKeys || [])
                    .map(getItemKey)
                    .filter(Boolean),
            ),
        [highlightedEventKeys],
    );

    const renderTileContent = ({ date, view }) => {
        if (view !== "month") {
            return null;
        }

        const dayEvents = events.filter((event) =>
            dayjs(event.date).isSame(dayjs(date), "day"),
        );

        if (dayEvents.length === 0) {
            return <Box sx={{ height: 10 }} />;
        }

        return (
            <Box
                sx={{
                    height: 10,
                    mt: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.35,
                }}
            >
                {dayEvents.slice(0, 3).map((event) => (
                    <Box
                        key={event.key}
                        component="span"
                        sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            backgroundColor: highlightedKeys.has(event.key)
                                ? "primary.main"
                                : getCalendarEventColor(event.color),
                            boxShadow: highlightedKeys.has(event.key)
                                ? "0 0 0 2px rgba(0, 149, 61, 0.18)"
                                : "none",
                        }}
                    />
                ))}
                {dayEvents.length > 3 && (
                    <Typography
                        component="span"
                        sx={{ fontSize: 9, lineHeight: 1 }}
                    >
                        +{dayEvents.length - 3}
                    </Typography>
                )}
            </Box>
        );
    };

    const tileClassName = ({ date, view }) => {
        if (view !== "month") {
            return "";
        }

        const hasHighlightedEvent = events.some(
            (event) =>
                highlightedKeys.has(event.key) &&
                dayjs(event.date).isSame(dayjs(date), "day"),
        );

        return hasHighlightedEvent ? "nodiant-calendar-linked-date" : "";
    };

    return (
        <Box
            sx={[
                calendarSx,
                spacious && {
                    "& .react-calendar__tile": {
                        minHeight: { xs: 64, sm: 82 },
                    },
                },
            ]}
        >
            <Calendar
                value={selectedDate}
                // In range mode a click is only half an answer, so the whole
                // range arrives through onChange instead of a per-day click.
                {...(selectRange
                    ? { selectRange: true, onChange: onDateChange }
                    : { onClickDay: onDateChange })}
                minDate={minDate}
                maxDate={maxDate}
                tileContent={renderTileContent}
                tileClassName={tileClassName}
                locale="en-GB"
                prev2Label={null}
                next2Label={null}
            />
        </Box>
    );
}

const calendarSx = {
    "& .react-calendar": {
        width: "100%",
        border: "none",
        borderRadius: 0,
        boxShadow: "none",
        color: "text.primary",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        lineHeight: 1.3,
    },
    "& .react-calendar button": {
        border: "none",
        fontFamily: "inherit",
    },
    "& .react-calendar__navigation": {
        height: 46,
        marginBottom: 1,
        display: "flex",
        gap: 0.5,
    },
    "& .react-calendar__navigation button": {
        borderRadius: "10px !important",
        fontWeight: 800,
        fontSize: 16,
    },
    "& .react-calendar__navigation button:enabled:hover, & .react-calendar__navigation button:enabled:focus":
        {
            backgroundColor: "action.hover",
        },
    "& .react-calendar__navigation button[disabled], & .react-calendar__tile:disabled":
        {
            color: "text.disabled",
            backgroundColor: "action.disabledBackground",
        },
    "& .react-calendar__month-view__weekdays": {
        color: "text.secondary",
        fontSize: 12,
        fontWeight: 800,
        textDecoration: "none",
    },
    "& .react-calendar__month-view__weekdays abbr": {
        textDecoration: "none",
    },
    "& .react-calendar__tile": {
        minHeight: 58,
        padding: "9px 4px 5px",
        borderRadius: "10px !important",
        color: "text.primary",
        fontSize: 14,
    },
    "& .react-calendar__month-view__days__day--neighboringMonth": {
        color: "text.disabled",
    },
    "& .react-calendar__month-view__days__day--weekend": {
        color: "text.primary",
    },
    "& .react-calendar__tile:enabled:hover, & .react-calendar__tile:enabled:focus":
        {
            backgroundColor: "action.hover",
        },
    "& .react-calendar__tile--now": {
        color: "primary.main",
        backgroundColor: "action.hover",
        fontWeight: 800,
    },
    "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled:hover, & .react-calendar__tile--active:enabled:focus":
        {
            color: "white",
            backgroundColor: "primary.dark",
        },
    "& .react-calendar__tile--active .MuiBox-root": {
        color: "white",
    },
    "& .nodiant-calendar-linked-date": {
        boxShadow: "inset 0 0 0 1px rgba(0, 149, 61, 0.32)",
    },

    // Range selection. react-calendar marks every tile in the range --active
    // too, so these have to come after the --active rules above: the days
    // between the ends get a soft tint and square edges so the run reads as one
    // block, then the ends are painted back in solid.
    "& .react-calendar__tile--range": {
        color: "text.primary",
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
        borderRadius: "0 !important",
    },
    "& .react-calendar__tile--range .MuiBox-root": {
        color: "text.primary",
    },
    "& .react-calendar__tile--rangeStart, & .react-calendar__tile--rangeEnd": {
        color: "white",
        backgroundColor: "primary.dark",
    },
    "& .react-calendar__tile--rangeStart .MuiBox-root, & .react-calendar__tile--rangeEnd .MuiBox-root":
        {
            color: "white",
        },
    "& .react-calendar__tile--rangeStart": {
        borderTopLeftRadius: "10px !important",
        borderBottomLeftRadius: "10px !important",
    },
    "& .react-calendar__tile--rangeEnd": {
        borderTopRightRadius: "10px !important",
        borderBottomRightRadius: "10px !important",
    },
    "& .react-calendar__tile--rangeBothEnds": {
        borderRadius: "10px !important",
    },
    // The live preview between the first click and the second.
    "& .react-calendar--selectRange .react-calendar__tile--hover": {
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.09),
        borderRadius: "0 !important",
    },
    "& .react-calendar--selectRange .react-calendar__tile--hoverStart": {
        borderTopLeftRadius: "10px !important",
        borderBottomLeftRadius: "10px !important",
    },
    "& .react-calendar--selectRange .react-calendar__tile--hoverEnd": {
        borderTopRightRadius: "10px !important",
        borderBottomRightRadius: "10px !important",
    },
};
